import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { ThreeDots } from '@components/ui';
import { ThreeDotItem } from '@components/ui/ThreeDots/ThreeDots';
import {
  ApplicationType,
  UserFile,
  UserQuestionnaire,
  UserQuestionnaireApplicationTopic,
  UserTopicApplications,
} from '@dieterApi/user/useUserQuery';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EjectIcon from '@mui/icons-material/Eject';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Chip, FormControl, IconButton, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import cx from 'classnames';
import { useEntryHighlight } from '@app/hooks/useEntryHighlight';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { AlertDialog } from '../../../../../components/ui/AlertDialog/AlertDialog';
import { Loading } from '../../../../../components/ui/Loading/Loading';
import { Spinner } from '../../../../../components/ui/Spinner/Spinner';
import { ManageSubcontractorsModal } from './ManageSubcontractorsModal';

// Performance logging utility
const perfLog = {
  start: (label: string) => {
    if (process.env.NODE_ENV === 'development') {
      performance.mark(`${label}-start`);
    }
  },
  end: (label: string, logToConsole = true) => {
    if (process.env.NODE_ENV === 'development') {
      performance.mark(`${label}-end`);
      performance.measure(label, `${label}-start`, `${label}-end`);
      const measure = performance.getEntriesByName(label)[0];
      if (logToConsole && measure) {
        console.log(`⏱️ [${label}]: ${measure.duration.toFixed(2)}ms`);
      }
      return measure?.duration;
    }
    return 0;
  },
  group: (label: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.group(`📊 ${label}`);
    }
  },
  groupEnd: () => {
    if (process.env.NODE_ENV === 'development') {
      console.groupEnd();
    }
  },
};

import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { getTopicStates } from '@app/utils/getTopicStates';
import { useFileDownloadQuery } from '@dieterApi/file/useFileDownloadQuery';
import { useFileShareTokenQuery } from '@dieterApi/file/useFileShareTokenQuery';
import { useAddDieterAVVMutation } from '@dieterApi/questionnaire/useQuestionnaireAddDieterAVVMutation';
import { ISignatureRequest } from '@dieterApi/signature/useSignatureRequestCreateMutation';
import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import { useUserValueDeleteMutation } from '@dieterApi/user/useUserValueDeleteMutation';
import { AddContractModal } from './AddContractModal';
import { AddPartnerButton } from './AddPartnerButton';
import { RequestTomsModal } from './RequestTomsModal';
import { SignatureRequests } from './SignatureRequests';
import { extractPartnerData } from './extractPartnerData';
import './listentry.sass';
import {
  ContractDirection,
  ContractType,
  ListEntryContractType,
  PartnerTableMeta,
  contractDirectionMapping,
  contractTypeMapping,
  validContractTypes,
} from './typesAndEnums';

interface Props {
  topic: UserTopicApplications;
  topicStates: ReturnType<typeof getTopicStates>;
}

// Memoized Address icon to avoid re-creation on every render
const AddressIcon = memo(({ address }: { address?: string }) => (
  <Tooltip title={address ? address : 'Keine Adresse hinterlegt'}>
    <span>
      <IconButton size="small" color={address ? 'primary' : 'default'}>
        <LocationOnIcon style={{ opacity: address ? 1 : 0.5 }} />
      </IconButton>
    </span>
  </Tooltip>
));

// Memoized status chip button to avoid re-rendering heavy tooltip+chip tree
const StatusChipButton = memo(function StatusChipButton({
  isSigned,
  signaturePartial,
  onClick,
  testId,
}: {
  isSigned: boolean;
  signaturePartial: boolean;
  onClick: () => void;
  testId: string;
}) {
  const label = isSigned ? 'Signiert' : signaturePartial ? 'Teilsigniert' : 'Nicht signiert';
  const color = (isSigned ? 'success' : signaturePartial ? 'warning' : 'error') as any;
  return (
    <Tooltip
      title={isSigned ? 'Unterschrieben – Signaturdetails anzeigen' : 'Nicht signiert – Signatur anfordern/verwalten'}
    >
      <span>
        <button
          style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
          onClick={onClick}
          data-testid={testId}
        >
          <Chip
            label={<span style={{ textDecoration: 'underline' }}>{label}</span>}
            color={color}
            size="small"
            sx={{ color: 'white' }}
          />
        </button>
      </span>
    </Tooltip>
  );
});

// Memoized actions menu to avoid recreating ThreeDots items on each render of the cell
const RowActionsMenu = memo(function RowActionsMenu({
  partnerName,
  rowIndex,
  rowData,
  setDeleteRow,
  setDeleteAlertOpen,
  setCurrentTableMeta,
  setRequestTomsModalOpen,
  setManageSubModalOpen,
}: {
  partnerName: string;
  rowIndex: number;
  rowData: any[];
  setDeleteRow: (meta: any) => void;
  setDeleteAlertOpen: (open: boolean) => void;
  setCurrentTableMeta: (meta: any) => void;
  setRequestTomsModalOpen: (open: boolean) => void;
  setManageSubModalOpen: (open: boolean) => void;
}) {
  const direction = rowData[6];
  const items = useMemo<ThreeDotItem[]>(() => {
    const tm = { rowData, rowIndex } as any;
    const base: ThreeDotItem[] = [
      {
        label: 'Vertragspartner löschen',
        onClick: () => {
          setDeleteRow(tm);
          setDeleteAlertOpen(true);
        },
        icon: <DeleteIcon fontSize="small" />,
        testlabel: `button-delete-partner`,
      },
      {
        label: 'Partner TOMS verwalten',
        onClick: () => {
          setCurrentTableMeta(tm);
          setRequestTomsModalOpen(true);
        },
        icon: <ShareIcon fontSize="small" />,
      },
    ];
    if (direction === 'Ich bin der Auftragnehmer') {
      base.push({
        label: 'Subunternehmer verwalten',
        onClick: () => {
          setCurrentTableMeta(tm);
          setManageSubModalOpen(true);
        },
        icon: <InfoIcon fontSize="small" />,
        testlabel: `button-manage-subcontractors`,
      });
    }
    return base;
  }, [
    direction,
    rowData,
    rowIndex,
    setCurrentTableMeta,
    setDeleteAlertOpen,
    setDeleteRow,
    setManageSubModalOpen,
    setRequestTomsModalOpen,
  ]);

  return <ThreeDots items={items} data-testid={`threedots-${partnerName}`} />;
});

export const PartnerListEntry = memo(
  function PartnerListEntry({ topic, topicStates }: Props) {
    // Track render count in development
    const renderCountRef = useRef(0);
    const prevPropsRef = useRef<Props | null>(null);
    renderCountRef.current++;

    perfLog.start('PartnerListEntry-render');
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 PartnerListEntry render #${renderCountRef.current}`);

      // Log what changed
      if (prevPropsRef.current) {
        const prev = prevPropsRef.current;
        if (prev.topic !== topic) console.log('  ↳ topic object changed (ref)');
        if (prev.topic.id !== topic.id) console.log('  ↳ topic.id changed:', prev.topic.id, '→', topic.id);
        if (prev.topicStates !== topicStates) console.log('  ↳ topicStates object changed (ref)');
        if (prev.topicStates.isLocked !== topicStates.isLocked) console.log('  ↳ topicStates.isLocked changed');
      }
      prevPropsRef.current = { topic, topicStates };
    }

    const { user, activeCompany, topics, questionnaires, isDpoMode, whitelabelConfig } = useUserWithCompany();
    const avatarName = whitelabelConfig?.avatarName || 'Dieter';
    const { userValues } = useUserValuesData();
    const dashboard = useDashboard();
    const blankEntry = { partnerName: '', contractType: undefined, index: '', contractLocked: false };
    const [newEntry, setNewEntry] = useImmer<PartnerData>(blankEntry);
    const {
      navigation: { roadBlock },
      setNavigation,
    } = useNavigation();

    const [downloadFile] = useFileDownloadQuery();
    const [deleteRow, setDeleteRow] = useState<MUIDataTableMeta | null>(null);
    const [showSignaturesRow, setShowSignaturesRow] = useState<string | null>(null);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [requestTomsModalOpen, setRequestTomsModalOpen] = useState(false);
    const [currentTableMeta, setCurrentTableMeta] = useState<PartnerTableMeta>();
    // Local ref to avoid re-rendering on each key press for manual partner name input
    const manualNameRef = useRef<HTMLInputElement | null>(null);
    const contractApps = topic.applications.filter((app) => app.type === ApplicationType.ListContract);
    const [addDieterAVV] = useAddDieterAVVMutation();
    const [deleteUserValue] = useUserValueDeleteMutation();
    const [addUserValue] = useUserValueAddMutation();
    const [dieterAVVIndex, setDieterAVVIndex] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const [getFileUploadToken, { data: uploadTokenData }] = useFileShareTokenQuery();

    // Memoize partnerData extraction to avoid expensive recalculation
    const partnerData = useMemo(() => {
      perfLog.start('partnerData-extraction');
      if (!user || !activeCompany || !userValues) return [];

      perfLog.start('extractPartnerData-call');
      const extracted = extractPartnerData(user, userValues, activeCompany, topics, questionnaires);
      perfLog.end('extractPartnerData-call');

      const result = newEntry.index ? [...extracted, newEntry] : extracted;
      perfLog.end('partnerData-extraction');
      if (process.env.NODE_ENV === 'development') {
        console.log(`📦 Partner data count: ${result.length}`);
      }
      return result;
    }, [newEntry, userValues, user, activeCompany, topics, questionnaires]);

    // Memoize affects checks for all rows to avoid expensive lookups during render
    const rowAffects = useMemo(() => {
      perfLog.start('rowAffects-calculation');
      const map = new Map<string, boolean>();
      let affectsCallCount = 0;

      partnerData.forEach((p) => {
        if (p.contract?.id) {
          map.set(p.contract.id, dashboard.affects(undefined, p.contract.id));
          affectsCallCount++;
        }
        if (p.index) {
          map.set(p.index, dashboard.affects(undefined, p.index));
          affectsCallCount++;
        }
      });

      perfLog.end('rowAffects-calculation');
      console.log(`🔍 Affects checks performed: ${affectsCallCount}`);
      return map;
    }, [partnerData, dashboard.affects]);

    const affectsCheck = useCallback(
      (id?: string) => {
        if (!id) return false;
        return rowAffects.get(id) || false;
      },
      [rowAffects]
    );

    const dieterAVVExisting = !!userValues?.find(
      (uv) => uv.key === 'DATA_PROCESSOR_NAME' && uv.value === 'simply Legal GmbH'
    );

    const handleCreateContract = (tableMeta: PartnerTableMeta) => {
      const index = tableMeta.rowData[2];
      const contractType = tableMeta.rowData[1];
      const contractApp =
        contractType &&
        contractApps.find((app) => app.tags?.map((tag) => tag.tag).includes(contractType as ContractType));

      if (contractApp) {
        index &&
          dashboard.create(
            contractApp.id,
            undefined,
            true,
            index // this is the link to the table row (partner)
          );
      } else {
        enqueueSnackbar('Vertragstyp wird noch nicht unterstützt.', { variant: 'error' });
        setCreateModalOpen(false);
      }
    };

    const [signLoading, setSignLoading] = useState(false);

    const handleSignContract = (index: string | null) => {
      if (!index) return;
      setSignLoading(true);
      addUserValue({
        variables: {
          key: 'DATA_PROCESSOR_CONTRACT_SIGNED',
          value: 'true',
          index,
        },
      }).finally(() => {
        setSignLoading(false);
        setDieterAVVIndex(null);
      });
    };

    const [dieterAvvAddLoading, setDieterAvvAddLoading] = useState(false);
    const handleAddDieterAVV = () => {
      setDieterAvvAddLoading(true);
      addDieterAVV().finally(() => {
        setDieterAvvAddLoading(false);
      });
    };

    const handleAddPartner = useCallback(
      (partnerNameArg?: string) => {
        const partnerName = (partnerNameArg ?? manualNameRef.current?.value ?? newEntry.partnerName)?.trim();
        const variables = { key: 'DATA_PROCESSOR_NAME', value: partnerName || '', createIndex: true };
        partnerName &&
          newEntry.contractType &&
          addUserValue({
            variables,
            // context: { userId: user?.id || '' },
          })
            .then((result) => {
              const index = result.data?.addUserValue.index;
              if (newEntry.contractType) {
                const variables = { key: 'DATA_PROCESSOR_TYPE', value: newEntry.contractType, index };
                addUserValue({
                  variables,
                  // context: { userId: user?.id || '' },
                });
              }
              if (newEntry.direction) {
                const variables = { key: 'DATA_PROCESSOR_DIRECTION', value: newEntry.direction, index };
                addUserValue({
                  variables,
                  // context: { userId: user?.id || '' },
                });
              }
            })
            .then(() => {
              setNewEntry({ partnerName: '', contractType: undefined, index: '', contractLocked: false });
              // Clear the uncontrolled input field if it still exists
              if (manualNameRef.current) manualNameRef.current.value = '';
            })
            .catch((e) => {
              enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
            });
      },
      [newEntry.partnerName, newEntry.contractType, newEntry.direction, addUserValue, setNewEntry, enqueueSnackbar]
    );

    // Auto-save when direction is selected for a manual entry with a non-empty name
    const autoSaveTriggeredRef = useRef(false);
    useEffect(() => {
      if (newEntry.index === 'MANUAL' && newEntry.direction && newEntry.contractType) {
        const partnerName = manualNameRef.current?.value?.trim();
        if (partnerName && !autoSaveTriggeredRef.current) {
          autoSaveTriggeredRef.current = true;
          handleAddPartner(partnerName);
        }
      } else {
        autoSaveTriggeredRef.current = false;
      }
    }, [newEntry.direction, newEntry.index, newEntry.contractType, handleAddPartner]);

    useEffect(() => {
      if (uploadTokenData?.getFileShareToken) {
        const token = uploadTokenData.getFileShareToken;
      }
    }, [uploadTokenData]);

    const handleShowContract = useCallback(
      (tableMeta: PartnerTableMeta) => {
        const contractLocked = tableMeta.rowData[9];
        // we allow viewing the simply Legal contract
        if (topicStates.isLocked && !contractLocked) {
          roadBlock.open(topic);
        }
        const index = tableMeta.rowData[2] as string;
        const partnerName = tableMeta.rowData[0] as string;

        // only temporary in here, before we have proper UI
        getFileUploadToken({
          variables: {
            type: 'upload',
            topicId: topic.id,
            description: `${partnerName} - Technische und Organisatorische Maßnahmen`,
            userValueIndex: index,
          },
        });

        const questionnaire = tableMeta.rowData[4];

        questionnaire &&
          setNavigation((nav) => {
            nav.documentPaper = {
              modalOpen: true,
              questionnaire,
              index: 0,
            };
          });
      },
      [getFileUploadToken, roadBlock, setNavigation, topic, topicStates.isLocked]
    );

    const handleOpenContract = useCallback(
      (tableMeta: PartnerTableMeta) => {
        const questionnaire = tableMeta.rowData[4];
        questionnaire && dashboard.open(questionnaire.id);
      },
      [dashboard]
    );

    const handleDownloadToms = (tableMeta: PartnerTableMeta): void => {
      if (topicStates.isLocked) {
        roadBlock.open(topic);
        return;
      }
      const toms = tableMeta.rowData[5]!;
      downloadFile({ variables: { id: toms.id } });
    };

    const handleRemoveFileAttachment = useCallback(
      (tableMeta: PartnerTableMeta) => {
        const index = tableMeta.rowData[2];
        if (!index) return;
        deleteUserValue({
          variables: { key: 'DATA_PROCESSOR_CONTRACTFILE', index },
          context: { userId: user?.id || '' },
        });
      },
      [deleteUserValue, user?.id]
    );

    const handleDownloadFile = useCallback(
      (file: UserFile) => {
        if (topicStates.isLocked) {
          roadBlock.open(topic);
          return;
        }
        downloadFile({ variables: { id: file.id } });
      },
      [downloadFile, roadBlock, topic, topicStates.isLocked]
    );

    const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
    const [currentAddressMeta, setCurrentAddressMeta] = useState<PartnerTableMeta | null>(null);
    const addressTextareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Stable column index for 'address' column
    const ADDRESS_COL_INDEX = 12;

    const handleOpenAddressModal = useCallback((tableMeta: PartnerTableMeta) => {
      setCurrentAddressMeta(tableMeta);
      setEditAddressModalOpen(true);
    }, []);

    const handleSaveAddress = () => {
      if (!currentAddressMeta) return;
      const index = currentAddressMeta.rowData[2] || '';
      const value = addressTextareaRef.current?.value || '';
      addUserValue({
        variables: { key: 'DATA_PROCESSOR_ADDRESS', value, index },
        // context: { userId: user?.id || '' },
      }).then(() => setEditAddressModalOpen(false));
    };

    // (Removed duplicate AddressIcon; using memoized AddressIcon defined above)

    // State for Subunternehmer modal
    const [manageSubModalOpen, setManageSubModalOpen] = useState(false);

    // Track sort order to prevent reset on data changes
    const [sortOrder, setSortOrder] = useState<{ name: string; direction: 'asc' | 'desc' }>({
      name: 'partnerName',
      direction: 'asc',
    });

    // Stable helper to open signatures details given row index and data
    const openSignaturesFor = useCallback((rowIndex: number, rowData: any[]) => {
      const tm = { rowData, rowIndex } as any;
      const contractIndex = tm.rowData[2]; // The unique contract ID
      if (process.env.NODE_ENV === 'development') {
        console.log('Opening signatures for contract:', contractIndex, 'rowIndex:', rowIndex);
      }

      setShowSignaturesRow(contractIndex);
      setCurrentTableMeta(tm as PartnerTableMeta);
    }, []);

    const columns: MUIDataTableProps['columns'] = useMemo(() => {
      perfLog.start('columns-definition');
      const cols: MUIDataTableProps['columns'] = [
        {
          // 0
          name: 'partnerName',
          label: 'Name',
          options: {
            customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
              perfLog.start(`render-partnerName-row${tableMeta.rowIndex}`);
              const address = tableMeta.rowData[12];

              const isManual = tableMeta.rowData[2] == 'MANUAL';
              const result = (
                <div className="flex items-center gap-2 justify-between">
                  <div>
                    {isManual ? (
                      <input
                        autoFocus
                        // placeholder="Partner name"
                        ref={manualNameRef}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddPartner(manualNameRef.current?.value || undefined);
                          }
                        }}
                        data-testid="input-partner-name"
                      />
                    ) : (
                      value
                    )}
                  </div>
                  <span
                    onClick={() => handleOpenAddressModal(tableMeta as PartnerTableMeta)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AddressIcon address={address} />
                  </span>
                </div>
              );
              perfLog.end(`render-partnerName-row${tableMeta.rowIndex}`, false);
              return result;
            },
          },
        },
        {
          // 1
          name: 'contractType',
          label: 'Vertragstyp',
          options: {
            customBodyRender: (value: Partial<ContractType>, tableMeta: MUIDataTableMeta) => {
              perfLog.start(`render-contractType-row${tableMeta.rowIndex}`);
              const isManual = tableMeta.rowData[2] == 'MANUAL';
              const result = isManual ? (
                <FormControl variant="standard">
                  <Select
                    value={newEntry?.contractType}
                    onChange={(e) => setNewEntry((draft) => void (draft && (draft.contractType = e.target.value)))}
                  >
                    {Object.entries(contractTypeMapping).map(([short, long]) => {
                      return (
                        <MenuItem key={short} value={short}>
                          {long}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : (
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={contractTypeMapping[value] || value}>
                  <span>{value}</span>
                </Tooltip>
              );
              perfLog.end(`render-contractType-row${tableMeta.rowIndex}`, false);
              return result;
            },
          },
        },
        {
          // 2
          name: 'index',
          options: {
            display: 'excluded',
          },
        },
        {
          // 3
          name: 'contractFile',
          options: {
            display: 'excluded',
          },
        },
        {
          // 4
          name: 'contract',
          options: {
            display: 'excluded',
          },
        },
        {
          // 5
          name: 'toms',
          options: {
            display: 'excluded',
          },
        },
        {
          // 6
          name: 'direction',
          label: 'Richtung',
          options: {
            customBodyRender: (value: ContractDirection | undefined, tableMeta: MUIDataTableMeta) => {
              const name = tableMeta.rowData[0];
              const index = tableMeta.rowData[2];
              const isLockedForEditing = !!tableMeta.rowData[9];
              const isManual = index == 'MANUAL';
              return !isLockedForEditing || !value ? (
                <FormControl variant="standard" error={!value}>
                  <Select
                    value={(isManual ? newEntry?.direction : value) || ''}
                    onChange={
                      isManual
                        ? (e) =>
                            setNewEntry(
                              (draft) => void (draft && (draft.direction = e.target.value as ContractDirection))
                            )
                        : (e) => {
                            const variables = {
                              key: 'DATA_PROCESSOR_DIRECTION',
                              value: e.target.value,
                              index,
                            };
                            addUserValue({
                              variables,

                              // context: {
                              //   userId: user?.id || '',
                              // },
                            });
                          }
                    }
                    data-testid={`select-direction-${name}`}
                  >
                    {Object.entries(contractDirectionMapping).map(([long, short]) => {
                      return (
                        <MenuItem key={short} value={long}>
                          {long}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : (
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={value}>
                  <span>{contractDirectionMapping[value] || value}</span>
                </Tooltip>
              );
            },
          },
        },
        {
          // 7
          name: 'actions',
          label: 'Vertrag',
          options: {
            setCellHeaderProps(columnMeta) {
              return { style: { fontSize: '30px' } };
            },
            customBodyRender: (value, tableMeta: MUIDataTableMeta) => {
              perfLog.start(`render-actions-row${tableMeta.rowIndex}`);
              const contractType = tableMeta.rowData[1];
              const index = tableMeta.rowData[2];
              const isManual = index === 'MANUAL';
              const contractFile = tableMeta.rowData[3] as UserFile;
              const hasContractFile = contractFile && contractFile.id;
              const contract = tableMeta.rowData[4] as unknown as UserQuestionnaireApplicationTopic;
              const hasDirection = tableMeta.rowData[6] as ContractDirection;
              const isLockedForEditing = !!tableMeta.rowData[9];
              const signatureRequest = tableMeta.rowData[13] as ISignatureRequest | undefined;
              const signedContractFile = tableMeta.rowData[11] as UserFile | undefined;

              const result = hasContractFile ? (
                <>
                  <Tooltip
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                    title={`Vertrag herunterladen (${contractFile.name})`}
                  >
                    <IconButton size="small" onClick={() => handleDownloadFile(contractFile)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {signatureRequest || signedContractFile ? (
                    <Tooltip
                      title={
                        signatureRequest
                          ? 'Datei kann nicht entfernt werden, solange eine Signaturanfrage existiert.'
                          : 'Datei kann nicht entfernt werden, da ein unterschriebener Vertrag hochgeladen wurde.'
                      }
                    >
                      <span>
                        <IconButton size="small" disabled>
                          <EjectIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title={`Vertrag entfernen (${contractFile.name})`}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFileAttachment(tableMeta as PartnerTableMeta)}
                      >
                        <EjectIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : isManual ? (
                <Link to="#" onClick={() => setNewEntry(blankEntry)}>
                  Abbrechen
                </Link>
              ) : affectsCheck(contract?.id) || affectsCheck(index) ? (
                <Spinner size="small" />
              ) : contract ? (
                contract.progress == 1 ? (
                  <>
                    <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Vertrag anzeigen">
                      <IconButton size="small" onClick={() => handleShowContract(tableMeta as PartnerTableMeta)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Vertrag herunterladen">
                  <IconButton size="small" onClick={() => handleDownloadContract(tableMeta as PartnerTableMeta)}>
                    <Icon type="doc-16" size={16} />
                  </IconButton>
                </Tooltip> */}
                    {!isLockedForEditing && (
                      <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Vertrag bearbeiten">
                        <IconButton size="small" onClick={() => handleOpenContract(tableMeta as PartnerTableMeta)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Link to="#" onClick={() => handleOpenContract(tableMeta as PartnerTableMeta)}>
                    Fortsetzen
                  </Link>
                )
              ) : (
                hasDirection &&
                validContractTypes.includes(contractType) && (
                  <Link
                    to="#"
                    onClick={() => {
                      setCreateModalOpen(true);
                      setCurrentTableMeta(tableMeta as PartnerTableMeta);
                    }}
                  >
                    Jetzt anlegen
                  </Link>
                )
              );
              perfLog.end(`render-actions-row${tableMeta.rowIndex}`, false);
              return result;
            },
          },
        },
        {
          // 8
          name: 'contractSigned',
          label: 'Status',

          options: {
            customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
              perfLog.start(`render-contractSigned-row${tableMeta.rowIndex}`);
              // do not show status if no contract is yet created (either via questionnaire or uploaded)
              const contractFile = tableMeta.rowData[3];
              const questionnaire = tableMeta.rowData[4] as UserQuestionnaire;
              const isLockedForEditing = !!tableMeta.rowData[9];
              const questionnaireCompleted = questionnaire && questionnaire.state === 'COMPLETE';
              if (
                (!contractFile && !questionnaire) ||
                isLockedForEditing ||
                (questionnaire && !questionnaireCompleted)
              ) {
                return <span />;
              }

              // Status is signed if either a signatureRequest is fulfilled or a signed contract file exists
              const index = tableMeta.rowData[2];
              const signedContractFile = tableMeta.rowData[11];
              // Get signatureRequest directly from rowData (hidden column)
              const signatureRequest = tableMeta.rowData[13] as ISignatureRequest | undefined;

              const signatureFulfilled = signatureRequest && signatureRequest.hasSigned1 && signatureRequest.hasSigned2;
              const signaturePartial = signatureRequest && (signatureRequest.hasSigned1 || signatureRequest.hasSigned2);
              const isSigned = Boolean(signatureFulfilled || signedContractFile);
              const result = (
                <StatusChipButton
                  isSigned={isSigned}
                  signaturePartial={!!signaturePartial}
                  onClick={() => openSignaturesFor(tableMeta.rowIndex, tableMeta.rowData)}
                  testId={`signature-status-btn-${index}`}
                />
              );
              perfLog.end(`render-contractSigned-row${tableMeta.rowIndex}`, false);
              return result;
            },
          },
        },

        {
          // 9
          name: 'contractLocked',
          options: {
            display: 'excluded',
          },
        },
        {
          // 10
          name: 'delete',
          label: 'Löschen',
          options: {
            customBodyRender: (value, tableMeta: MUIDataTableMeta) => {
              perfLog.start(`render-delete-row${tableMeta.rowIndex}`);
              const partnerName = tableMeta.rowData[0];
              const index = tableMeta.rowData[2];
              const isManual = tableMeta.rowData[2] == 'MANUAL';
              const isLockedForEditing = !!tableMeta.rowData[9];
              const isSigned = !!tableMeta.rowData[8];
              if (isLockedForEditing) {
                if (isSigned)
                  return (
                    <Tooltip title="Vertrag akzeptiert">
                      <CheckCircleIcon color="success" fontSize="small" />
                    </Tooltip>
                  );
                return signLoading ? (
                  <Spinner size="small" />
                ) : (
                  <button
                    className="underline text-primary-root hover:text-primary-700"
                    onClick={() => setDieterAVVIndex(index)}
                  >
                    Akzeptieren
                  </button>
                );
              }
              if (isManual)
                return (
                  <div className="flex">
                    <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Vertragspartner speichern">
                      <IconButton
                        className="dtListEntry__deleteButton"
                        color="info"
                        size="small"
                        onClick={() => handleAddPartner(manualNameRef.current?.value || undefined)}
                        data-testid={`buton-save-partner-${partnerName}`}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                );
              // Use memoized row actions menu to avoid per-render item array recreation
              const result = (
                <RowActionsMenu
                  partnerName={partnerName}
                  rowIndex={tableMeta.rowIndex}
                  rowData={tableMeta.rowData}
                  setDeleteRow={setDeleteRow as any}
                  setDeleteAlertOpen={setDeleteAlertOpen}
                  setCurrentTableMeta={setCurrentTableMeta as any}
                  setRequestTomsModalOpen={setRequestTomsModalOpen}
                  setManageSubModalOpen={setManageSubModalOpen}
                />
              );
              perfLog.end(`render-delete-row${tableMeta.rowIndex}`, false);
              return result;
            },
            customHeadLabelRender: () => '',
          },
        },
        {
          // 11
          name: 'signedContractFile',
          options: {
            display: 'excluded',
          },
        },
        {
          // 12
          name: 'address',
          label: 'Adresse',
          options: {
            display: 'excluded',
            customBodyRender: (value: string | undefined, tableMeta: MUIDataTableMeta) => {
              return <></>;
            },
          },
        },
        {
          // 13
          name: 'signatureRequest',
          options: {
            display: 'excluded',
          },
        },
      ];

      perfLog.end('columns-definition');
      return cols;
    }, [
      handleAddPartner,
      handleDownloadFile,
      handleOpenContract,
      handleOpenAddressModal,
      handleRemoveFileAttachment,
      handleShowContract,
      newEntry?.contractType,
      newEntry?.direction,
      affectsCheck,
      openSignaturesFor,
      signLoading,
    ]);

    const handleDelete = (tableMeta: MUIDataTableMeta) => {
      const [partnerName, contractType, index] = tableMeta.rowData as [string, string, string];
      const keys = [
        'DATA_PROCESSOR_NAME',
        'DATA_PROCESSOR_TYPE',
        'DATA_PROCESSOR_CONTRACTFILE',
        'DATA_PROCESSOR_DIRECTION',
        'DATA_PROCESSOR_ISSUBCONTRACTOR',
      ];
      keys.forEach((key) => deleteUserValue({ variables: { key, index }, context: { userId: user?.id || '' } }));
    };

    // Precompute reverse mapping to avoid work in setRowProps
    const contractTypeReverseMap = useMemo(() => {
      const m: Record<string, string> = {};
      Object.entries(contractTypeMapping).forEach(([key, value]) => (m[value as string] = key));
      return m;
    }, []);

    const { entry, rowProps } = useEntryHighlight();

    const setRowPropsCallback = useCallback(
      (row: any[], dataIndex: number, rowIndex: number) => {
        perfLog.start(`setRowProps-row${rowIndex}`);
        const thisRow = row as PartnerTableMeta['rowData'];
        const contractTypePretty = thisRow[1] as string;
        const contractType = contractTypeReverseMap[contractTypePretty];
        const contractFile = thisRow[3];
        const questionnaire = thisRow[4];
        const questionnaireCompleted = questionnaire && questionnaire.progress == 1;
        const doesNotNeedContract = contractType && !validContractTypes.includes(contractType as ListEntryContractType);
        const completed = Boolean(contractFile || questionnaireCompleted || doesNotNeedContract);
        const result = rowProps(
          thisRow[2] as string,
          cx({
            completed,
            inProgress: questionnaire && !questionnaireCompleted,
            hasNothing: !completed && !questionnaire,
          })
        );
        perfLog.end(`setRowProps-row${rowIndex}`, false);
        return result;
      },
      [contractTypeReverseMap, entry]
    );

    const toolbarRenderer = useCallback(() => {
      return (
        <div className="flex gap-4 items-center justify-end">
          <AddPartnerButton topic={topic} setNewEntry={setNewEntry} />
          <Tooltip
            title={`Du kannst einen Partner aus einer Liste der häufigsten Anbieter auswählen (Schnellauswahl), dir von ${avatarName} bei der Wahl des richtigen Vertrages helfen lassen (Geführter Dialog) oder einen Partner selbst anlegen (Manuelle Eingabe).`}
          >
            <InfoIcon className="text-gray-400" fontSize="small" />
          </Tooltip>
        </div>
      );
    }, [topic, avatarName]);

    const options: MUIDataTableOptions = useMemo(
      () => ({
        download: false,
        print: false,
        elevation: 0,

        // onRowClick: handleClick,
        selectableRows: 'none',
        selectableRowsHeader: false,

        // Controlled sort order to prevent reset on data changes
        sortOrder,
        // selectableRowsOnClick: true,
        search: true,
        filter: false,
        viewColumns: false,
        pagination: true,
        rowsPerPage: 15,
        setRowProps: setRowPropsCallback,
        onTableChange: (action, tableState) => {
          perfLog.start(`onTableChange-${action}`);
          console.log(`📋 Table action: ${action}`);

          // Track sort order changes
          if (action === 'sort' && tableState.sortOrder) {
            setSortOrder(tableState.sortOrder);
          }

          if (currentTableMeta) {
            // compare each entry of the currentTableMeta.rowData with the tableState.data
            // if it is different, update the currentTableMeta.rowData using a cheap version hash
            const currentRow = tableState.data[currentTableMeta.rowIndex];
            const versionOf = (row: any[]) => {
              // Pick only stable primitives/ids
              const contractFileId = row?.[3]?.id || '';
              const questionnaire = row?.[4];
              const questionnaireId = questionnaire?.id || '';
              const questionnaireProg = questionnaire?.progress ?? questionnaire?.state ?? '';
              const signedFileId = row?.[11]?.id || '';
              const address = row?.[12] || '';
              const sigReqHash = row?.[13]?.id || row?.[13]?.status || '';
              return `${row?.[0] || ''}|${row?.[1] || ''}|${row?.[2] || ''}|${contractFileId}|${questionnaireId}|${questionnaireProg}|${row?.[8] || ''}|${row?.[9] || ''}|${signedFileId}|${address}|${sigReqHash}`;
            };
            if (versionOf(currentRow.data) !== versionOf(currentTableMeta.rowData)) {
              setCurrentTableMeta({ ...currentTableMeta, rowData: currentRow.data });
            }
          }

          perfLog.end(`onTableChange-${action}`, false);
        },
        customToolbar: toolbarRenderer,
        // onRowsDelete: handleDelete,
        textLabels: {
          selectedRows: {
            text: 'Zeilen ausgewählt',
            delete: 'Löschen',
            deleteAria: 'Ausgewählte Zeilen löschen',
          },
          body: {
            noMatch: 'Keine Einträge vorhanden',
          },
        },
        expandableRows: true,
        isRowExpandable(dataIndex, expandedRows) {
          const contract = partnerData?.[dataIndex]?.contract;
          return contract?.progress == 1;
        },
        renderExpandableRow(rowData, rowMeta) {
          perfLog.start(`renderExpandableRow-${rowMeta.rowIndex}`);
          const colSpan = rowData.length + 1;
          const index = rowData[2];

          // Pass tableMeta, topic, and setManageSubModalOpen to SignatureRequests
          const result = (
            <SignatureRequests
              contractId={index}
              colSpan={colSpan}
              onClose={() => setShowSignaturesRow(null)}
              tableMeta={{ rowData, rowIndex: rowMeta.rowIndex }}
              topic={topic}
              setManageSubModalOpen={setManageSubModalOpen}
            />
          );
          perfLog.end(`renderExpandableRow-${rowMeta.rowIndex}`);
          return result;
        },
        rowsExpanded: showSignaturesRow !== null ? [partnerData.findIndex((p) => p.index === showSignaturesRow)] : [],
        expandableRowsOnClick: false,
        expandableRowsHeader: false,
      }),
      [currentTableMeta, partnerData, setRowPropsCallback, showSignaturesRow, sortOrder, topic, toolbarRenderer]
    );

    // Summary of render timing
    useEffect(() => {
      const renderTime = perfLog.end('PartnerListEntry-render', false);
      if (process.env.NODE_ENV === 'development') {
        perfLog.group('Component Render Summary');
        console.log(`🎯 Total render time: ${renderTime?.toFixed(2)}ms`);
        console.log(`📊 Partner count: ${partnerData?.length || 0}`);

        // Aggregate column render times
        if (partnerData?.length) {
          const measureEntries = performance.getEntriesByType('measure');
          const columnTimings: Record<string, number[]> = {};

          measureEntries.forEach((entry) => {
            const match = entry.name.match(/render-(\w+)-row\d+/);
            if (match) {
              const colName = match[1];
              if (!columnTimings[colName]) columnTimings[colName] = [];
              columnTimings[colName].push(entry.duration);
            }
          });

          Object.entries(columnTimings).forEach(([col, times]) => {
            const total = times.reduce((a, b) => a + b, 0);
            const avg = total / times.length;
            console.log(`  📌 ${col}: ${total.toFixed(2)}ms total, ${avg.toFixed(2)}ms avg per row`);
          });
        }

        perfLog.groupEnd();
      }

      // Clear old performance marks
      performance.clearMarks();
      performance.clearMeasures();
    });

    return (
      <div className="dtListEntry">
        {!isDpoMode && !dieterAVVExisting && ['Basic', 'Premium', 'Ticket'].includes(user?.subscription || '') && (
          <div className="bg-secondary-100 rounded-md p-5 text-secondary-700 text-sm flex flex-col gap-5 mb-8 items-start">
            <span>
              Neben deinen anderen Vertragspartnern benötigst du ebenfalls einen Auftrags&shy;verarbeitungs&shy;vertrag
              mit der <strong>simply Legal GmbH</strong>, den Betreibern der Dieter Datenschutz Plattform. Den nötigen
              Vertrag kannst du jetzt mit einem Klick deiner Partnerliste hinzufügen und anschließend akzeptieren.
            </span>
            {dieterAvvAddLoading ? (
              <Spinner size="small" />
            ) : (
              <button onClick={handleAddDieterAVV} className="underline text-primary-root hover:text-primary-700">
                Jetzt hinzufügen
              </button>
            )}
          </div>
        )}
        {partnerData ? (
          <MUIDataTable columns={columns} data={partnerData} title="Deine Vertragspartner" options={options} />
        ) : (
          <Loading />
        )}
        <AddContractModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreate={handleCreateContract}
          topic={topic}
          tableMeta={currentTableMeta}
        />
        {/* Address Edit Modal */}
        <AlertDialog
          open={editAddressModalOpen}
          setOpen={setEditAddressModalOpen}
          message={
            <>
              <div style={{ marginBottom: 8 }}>
                Adresse bearbeiten für <b>{currentAddressMeta?.rowData[0]}</b>:
              </div>
              <textarea
                ref={addressTextareaRef}
                key={`addr-${editAddressModalOpen}-${currentAddressMeta?.rowData?.[2] ?? ''}`}
                defaultValue={
                  typeof currentAddressMeta?.rowData?.[ADDRESS_COL_INDEX] === 'string'
                    ? (currentAddressMeta!.rowData[ADDRESS_COL_INDEX] as string)
                    : ''
                }
                className="w-full border border-gray-300 rounded-md p-2"
                placeholder="Adresse eingeben..."
                rows={3}
                data-testid="input-partner-address"
              />
            </>
          }
          onAccept={handleSaveAddress}
          acceptText="Speichern"
        />
        <RequestTomsModal
          open={requestTomsModalOpen}
          onClose={() => setRequestTomsModalOpen(false)}
          topic={topic}
          tableMeta={currentTableMeta}
        />
        {manageSubModalOpen && currentTableMeta && user && (
          <ManageSubcontractorsModal
            open={manageSubModalOpen}
            onClose={() => setManageSubModalOpen(false)}
            mainPartner={{
              partnerName: currentTableMeta.rowData[0],
              index: currentTableMeta.rowData[2]!,
            }}
            partnerData={partnerData}
            user={user}
            addUserValue={addUserValue}
            deleteUserValue={deleteUserValue}
            loading={false}
            setEditAddressModalOpen={setEditAddressModalOpen}
            setCurrentAddressMeta={setCurrentAddressMeta}
            setAddressDraft={() => {}}
            getPartnerTableMetaByIndex={(index: string) => {
              // Find the partner in partnerData and build a PartnerTableMeta-like object
              const partner = partnerData.find((p) => p.index === index);

              if (!partner) return null;
              // Simulate rowData as in PartnerTableMeta
              return {
                rowData: [
                  partner.partnerName,
                  partner.contractType || null,
                  partner.index,
                  partner.contractFile || null,
                  partner.contract || null,
                  partner.toms || null,
                  partner.direction || null,
                  null,
                  null,
                  partner.contractLocked || false,
                  null,
                  partner.signedContractFile || null,
                  partner.address || '',
                  partner.signatureRequest || null,
                ],
              };
            }}
          />
        )}
        <AlertDialog
          open={deleteAlertOpen}
          setOpen={setDeleteAlertOpen}
          message={
            <>
              Möchten Sie den Vertragspartner <b>{deleteRow?.rowData[0]}</b> wirklich löschen? Dadurch werden ebenfalls
              die erzeugten Verträge und Signiervorgänge gelöscht.
            </>
          }
          onAccept={() => deleteRow && handleDelete(deleteRow)}
        />
        <AlertDialog
          open={!!dieterAVVIndex}
          setOpen={() => setDieterAVVIndex(null)}
          message={
            <>
              Akzeptierst du den AV Vertrag mit der simply Legal GmbH (den Betreibern der Dieter-Platform) zur
              Verarbeitung personenbezogener Daten?
            </>
          }
          onAccept={() => handleSignContract(dieterAVVIndex)}
          acceptText="Ja"
        />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if topic.id or isLocked state actually changes
    // This prevents re-renders when parent passes new object references with same content
    const topicUnchanged = prevProps.topic.id === nextProps.topic.id;
    const lockedStateUnchanged = prevProps.topicStates.isLocked === nextProps.topicStates.isLocked;

    // Return true to SKIP re-render, false to allow it
    return topicUnchanged && lockedStateUnchanged;
  }
);

export interface PartnerData {
  partnerName: string;
  address?: string;
  contractType?: string;
  contractFile?: UserFile;
  signedContractFile?: UserFile;
  contractLocked?: boolean;
  contractSigned?: boolean;
  signatureRequest?: ISignatureRequest;
  contract?: UserQuestionnaireApplicationTopic;
  direction?: ContractDirection;
  index: string;
  isSubContractor?: boolean;
  toms?: UserFile;
}
